import React, { useEffect, useState } from "react";
import {
  setUserSettings,
  sendGreenlandVerification,
  verifyGreenlandCode,
} from "../services/api";
import Auth from "../services/auth";
import spinnerFrame01 from "../../images/loading-indicator/frame_01.webp";
import warning from "../../images/not-supported/icon.webp";
import auth0 from "auth0-js";
import { userPlan } from "../user";

const auth = new auth0.WebAuth({
  domain: process.env.AUTH0_DOMAIN,
  clientID: process.env.AUTH0_CLIENT_ID,
  redirectUri: window.location.origin,
  responseType: "token id_token",
  scope: "openid profile email",
});

// Greenland country code
const GREENLAND_COUNTRY_CODE = "+299";

const GreenlandPromo = () => {
  const userPlanId = userPlan();
  const isFreeUser = userPlanId === "NYA-FREE";

  const isClassicUser = [
    "NYA-UNLIMITED-MONTHLY",
    "NYA-UNLIMITED-YEARLY",
    "NYA-UNLIMITEDV2-YEARLY",
    "NYA-UNLIMITEDV2-MONTHLY",
  ].includes(userPlanId);
  const isPatronUser = ["NYA-UNLIMITED-YEARLY-PATRON"].includes(userPlanId);

  const isRustUser = [
    "NYA-UNLIMITED-YEARLY-RUST",
    "NYA-UNLIMITEDV2-YEARLY-RUST",
  ].includes(userPlanId);

  const [promoIsAvailable, setPromoIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [countdown, setCountdown] = useState(3);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("signup"); // signup, verification, success
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptEmails, setAcceptEmails] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSid, setVerificationSid] = useState("");

  const isFormValid =
    firstName.trim() !== "" &&
    phoneNumber.trim() !== "" &&
    email.trim() !== "" &&
    password.trim() !== "" &&
    confirmPassword.trim() !== "";

  const isVerificationFormValid = verificationCode.trim().length === 6;

  useEffect(() => {
    const checkAvailability = async () => {
      const isAvailable = true; // Promo is available for Greenland users
      setPromoIsAvailable(isAvailable);
      setIsLoading(false);
    };
    checkAvailability();
  }, []);

  useEffect(() => {
    if (step === "success" && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === "success" && countdown === 0) {
      window.location.href = "about-rust";
    }
  }, [step, countdown]);

  // Send verification code via Twilio
  const sendVerificationCodeToPhone = async (phone) => {
    try {
      const formattedPhone = phone.startsWith('+') 
        ? phone 
        : `${GREENLAND_COUNTRY_CODE}${phone}`;
      const result = await sendGreenlandVerification(formattedPhone);
      return result;
    } catch (err) {
      throw err;
    }
  };

  // Verify the code with Twilio
  const verifyPhoneCode = async (phone, code, userAuth0Id) => {
    try {
      const formattedPhone = phone.startsWith('+') 
        ? phone 
        : `${GREENLAND_COUNTRY_CODE}${phone}`;
      const result = await verifyGreenlandCode(
        formattedPhone,
        code,
        userAuth0Id
      );
      return result;
    } catch (err) {
      throw new Error(err.message || "Failed to verify code");
    }
  };

  // Handle subscription activation after verification
  const handleActivateSubscription = async () => {
    setIsLoading(true);
    setLoadingMessage("Verifying code and activating your subscription...");
    
    try {
      const userInfo = JSON.parse(localStorage.getItem("ade:user-info"));
      const result = await verifyPhoneCode(phoneNumber, verificationCode, userInfo.user_id);
      
      if (result.success) {
        setStep("success");
        setIsLoading(false);
      } else {
        if (result.errorCode === 'PHONE_ALREADY_USED') {
          window.displayWarningMessage("free-trial-rust-phone");
          setIsLoading(false);
          return;
        }
        setError("Invalid verification code. Please check the code and try again.");
        setIsLoading(false);
      }
    } catch (err) {
      if (err.response?.body?.errorCode === 'PHONE_ALREADY_USED') {
        window.displayWarningMessage("free-trial-rust-phone");
        setIsLoading(false);
        return;
      }
      setError(err.response?.body?.error || "Invalid verification code. Please check the code and try again.");
      setIsLoading(false);
    }
  };

  // Handle sign in after account creation
  const handleSignIn = (e) => {
    e?.preventDefault();
    setError("");
    setIsLoading(true);
    setLoadingMessage("Signing in...");

    auth.client.login(
      {
        realm: "Username-Password-Authentication",
        username: email,
        password: password,
      },
      (err, authResult) => {
        if (err) {
          setError(err.description || "An error occurred during sign-in.");
          setIsLoading(false);
          return;
        }

        auth.client.userInfo(authResult.accessToken, async (err, user) => {
          if (err) {
            setError("Could not get user information");
            setIsLoading(false);
            return;
          }

          // Set required local storage items
          localStorage.setItem("ade:id-token", authResult.idToken);
          localStorage.setItem("ade:orastream-token", authResult.accessToken);
          localStorage.setItem("ade:should-display-mailing-list-modal", "false");
          localStorage.setItem("ade:temp-token", authResult.accessToken);

          const userInfo = {
            email: user.email,
            name: user.name || user.email,
            app_metadata: {},
            clientId: process.env.AUTH0_CLIENT_ID,
            created_at: new Date().toISOString(),
            email_verified: false,
            identities: [
              {
                user_id: user.sub,
                provider: "auth0",
                connection: "Username-Password-Authentication",
                isSocial: false,
              },
            ],
            nickname: user.nickname || user.email.split("@")[0],
            picture: user.picture || `https://s.gravatar.com/avatar/${user.sub}`,
            sub: `auth0|${user.sub}`,
            updated_at: new Date().toISOString(),
            user_id: `auth0|${user.sub}`,
            user_metadata: {
              firstName: user.given_name || user.name?.split(" ")[0] || "",
              verification_email_sent: true,
            },
          };

          localStorage.setItem("ade:user-info", JSON.stringify(userInfo));
          localStorage.setItem("ade:uuid", user.sub);
          
         
          setLoadingMessage("Sending verification code to your phone...");
          
          try {
            const verificationResult = await sendVerificationCodeToPhone(phoneNumber);
            if (verificationResult.success) {
              
              if (acceptEmails) {
                setUserSettings({
                  type: "cdc_newsletter",
                  values: { subscribed: acceptEmails },
                });
              }
              setVerificationSid(verificationResult.sid);
              setStep("verification");
              setIsLoading(false);
            } else {
             
              if (verificationResult.errorCode === 'PHONE_ALREADY_USED') {
                window.displayWarningMessage("free-trial-rust-phone");
                setIsLoading(false);
                return;
              }
              setError(verificationResult.error || "Failed to send verification code");
              setIsLoading(false);
            }
          } catch (err) {

            if (err.response?.body?.errorCode === 'PHONE_ALREADY_USED') {
              window.displayWarningMessage("free-trial-rust-phone");
              setIsLoading(false);
              return;
            }
            setError(err.response?.body?.error || err.message || "Failed to send verification code. Please ensure you have a valid Greenland phone number.");
            setIsLoading(false);
          }
        });
      }
    );
  };

  // Handle sign up
  const handleSignUp = (e) => {
    e?.preventDefault();
    setError("");

    if (!firstName || !email || !password || !confirmPassword || !phoneNumber) {
      setError("Please fill out all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Basic phone number validation
    // Use GREENLAND_MODE to control whether to restrict to Greenland-only or allow international
    const isGreenlandOnly = process.env.GREENLAND_MODE === 'production';
    
    if (isGreenlandOnly) {
      // Production: Greenland numbers only (6 digits, no country code needed - it's added automatically)
      if (!/^\d{6}$/.test(phoneNumber)) {
        setError("Please enter a valid 6-digit Greenland phone number (without country code)");
        return;
      }
    } else {
     const isGreenlandFormat = /^\d{6}$/.test(phoneNumber);
      const isInternationalFormat = /^\+\d{1,3}\d{6,15}$/.test(phoneNumber);
      
      if (!isGreenlandFormat && !isInternationalFormat) {
        setError("Please enter a valid phone number (6 digits for Greenland, or full international format like +50498281588)");
        return;
      }
    }

    if (!isFreeUser && (isClassicUser || isPatronUser || isRustUser)) {
      window.displayWarningMessage("free-trial-rust");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadingMessage("Creating your account...");
    
    const formattedPhone = phoneNumber.startsWith('+') 
      ? phoneNumber 
      : `${GREENLAND_COUNTRY_CODE}${phoneNumber}`;
    
    auth.signup(
      {
        connection: "Username-Password-Authentication",
        email,
        password,
        user_metadata: { firstName, greenlandPhone: formattedPhone },
      },
      (err) => {
        if (err) {
          if (err.code === "user_exists") {
            window.displayWarningMessage("free-trial-rust");
            setIsLoading(false);
            return;
          } else {
            setError(err.description || "An error occurred during sign-up.");
            setIsLoading(false);
            return;
          }
        }

        // After successful signup, automatically sign in
        handleSignIn();
      }
    );
  };

  // Resend verification code
  const handleResendCode = async () => {
    setError("");
    setIsLoading(true);
    setLoadingMessage("Resending verification code...");
    
    try {
      const verificationResult = await sendVerificationCodeToPhone(phoneNumber);
      if (verificationResult.success) {
        setVerificationSid(verificationResult.sid);
        setIsLoading(false);
        setError(""); // Clear any previous errors
      } else {
        setError(verificationResult.error || "Failed to resend verification code");
        setIsLoading(false);
      }
    } catch (err) {
      setError(err.response?.body?.error || err.message || "Failed to resend verification code");
      setIsLoading(false);
    }
  };

  const renderSignUpForm = () => {
    return (
      <div className="signIn">
        <div className="form">
          <h3>
            INSTRUCTIONS FOR YOUR FREE SUBSCRIPTION
          </h3>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your name"
              required
            />
          </div>
          <div className="form-group">
            <label>Cellphone number:</label>
            <div className="phone-input-wrapper">
              {process.env.GREENLAND_MODE === 'production' && (
                <span className="country-code">{GREENLAND_COUNTRY_CODE}</span>
              )}
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  if (process.env.GREENLAND_MODE === 'production') {
                    setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 6));
                  } else {
                    setPhoneNumber(e.target.value.replace(/[^\d+]/g, ""));
                  }
                }}
                placeholder={
                  process.env.GREENLAND_MODE === 'production'
                    ? "Enter your 6-digit phone number"
                    : "Enter your phone number"
                }
                required
                className="phone-input"
              />
              
            </div>
            <p className="input-hint">
              We will text you back with a code to enter on this page and then delete your
              phone number from our records. Please allow up to 24 hours.
            </p>
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          <div className="form-group">
            <label>Password (verify)</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Password (verify)"
              required
            />
          </div>
          <div
            className="form-group"
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <input
              type="checkbox"
              id="acceptEmails"
              checked={acceptEmails}
              onChange={(e) => setAcceptEmails(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label
              htmlFor="acceptEmails"
              style={{ margin: 0, fontWeight: 400 }}
            >
              I would like to receive emails about Neil Young Archives and
              Rust offers.
            </label>
          </div>
          {error && <p className="error">{error}</p>}
          <div className="confirmation-buttons aligned">
            <button
              className="confirm-purchase"
              onClick={(e) => {
                if (!isFormValid) {
                  setError("Please fill out all fields to claim your subscription.");
                  e.preventDefault();
                  return;
                }
                handleSignUp(e);
              }}
            >
              NEXT - VERIFY PHONE
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVerificationForm = () => {
    return (
      <div className="signIn">
        <div className="form">
          <div className="form-group">
            <label>Verification Code</label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Enter 6-digit code"
              required
              maxLength={6}
              className="verification-input"
            />
          </div>
          {error && <p className="error">{error}</p>}
          <div className="confirmation-buttons aligned">
            <button
              className="confirm-purchase"
              onClick={(e) => {
                if (!isVerificationFormValid) {
                  setError("Please enter the 6-digit verification code.");
                  e.preventDefault();
                  return;
                }
                handleActivateSubscription();
              }}
            >
              VERIFY & ACTIVATE SUBSCRIPTION
            </button>
          </div>
          <div className="resend-section">
            <p>Didn't receive the code?</p>
            <button className="resend-button" onClick={handleResendCode}>
              Resend Code
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderInitialContent = () => {
    if (isLoading) {
      return (
        <div className="rust-container">
          <div className="rust-signup-container">
            <div className="follow-description">
              <p>{loadingMessage}</p>
            </div>
            <img
              className="spinner loading"
              src={spinnerFrame01}
              alt="spinner"
              loading="lazy"
            />
          </div>
        </div>
      );
    }

    if (!promoIsAvailable) {
      return (
        <div className="rust-container">
          <div className="rust-signup-container">
            <img
              src={warning}
              alt="Warning Message"
              width="64px"
              loading="lazy"
            />
            <br />
            <br />
            <div className="follow-description">
              <p>This promo is not available at this time.</p>
            </div>
            <div className="confirmation-buttons aligned">
              <div
                className="confirm-purchase"
                onClick={() => (location.href = "/")}
              >
                CONTINUE
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (step === "success") {
      return (
        <div className="rust-container">
          <div className="rust-signup-container">
            <div className="follow-description">
              <p>
                Congratulations! Your one-year{" "}
                <span style={{ fontFamily: "DinProBold" }}>NYA Rust</span> subscription
                has been activated!
              </p>
            </div>
            <br />
            <br />
            <img
              className="spinner loading"
              src={spinnerFrame01}
              alt="spinner"
              loading="lazy"
            />
            <div className="follow-description">
              <p>Redirecting in {countdown} seconds...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="rust-container">
        <div className="rust-signup-container">
          <div className="rust-header"></div>
          
          {step === "signup" && (
            <>
              <div className="greenland-welcome">
                <h2>To our Friends in Greenland,</h2>
                <div className="promo-tagline">
                <p>
                  As a gesture of kindness and respect, we stand with you along with a strong
                  majority of Americans.
                </p>
                <p>
                  I would like to offer a free one-year subscription to all Greenland residents.
                  I hope my Music and Music Films will ease some of the unwarranted stress and
                  threats you are experiencing from our unpopular and hopefully temporary government.
                </p>
                <p>
                  It is my sincere wish for you to be able to enjoy all my music in your beautiful
                  Greenland home, in its highest quality.
                </p>
                <p>
                  This is an offer of Peace and Love. All the music I have made during the last
                  62 years is yours to hear.
                </p>
                <p>
                  You can renew for free if you are in Greenland. We do hope other organizations
                  will follow in the spirit of our example.
                </p>
                </div>
              </div>
              <div className="rust-free-message">
                <p>
                  LOVE
                </p>
                <p>
                  Neil
                </p>
              </div>
              
              <div className="rust-description">
                <h3>INSTRUCTIONS FOR YOUR FREE SUBSCRIPTION</h3>
                <p>
                  Please enter your name, email, password, and cellphone number below.
                </p>
              </div>
            </>
          )}

          {step === "verification" && (
            <div className="verification-header">
              <h2>Enter the 6-digit verification code sent to your phone</h2>
              <p className="verification-info-main">
                We sent a verification code to {GREENLAND_COUNTRY_CODE} {phoneNumber}
              </p>
              <p className="verification-help">
                Please check your phone for a text message containing your verification code.
                Enter the 6-digit code below to complete your free subscription activation.
              </p>
            </div>
          )}

          <div className="description-section">
            {step === "signup" && renderSignUpForm()}
            {step === "verification" && renderVerificationForm()}
          </div>
          
          <div className="footer">
            <p className="promo-note">
              Our privacy policy can be found here:{" "}
              <a href="https://neilyoungarchives.com/privacy.html" target="_blank" rel="noreferrer">
                https://neilyoungarchives.com/privacy.html
              </a>
            </p>
            <p className="promo-note">
              Please email us at{" "}
              <a href="mailto:GREENLAND@NEILYOUNGARCHIVE.COM">
                GREENLAND@NEILYOUNGARCHIVE.COM
              </a>{" "}
              with any questions!
            </p>
          </div>
        </div>
      </div>
    );
  };

  return <div className="main-rust-container greenland-promo">{renderInitialContent()}</div>;
};

export default GreenlandPromo;
